"use client";

import { useRef } from "react";
import { SectionShell, useBandRowAlign, useWholeRowBlocks } from "@/components/sections/kit";
import { useEnterStagger } from "@/components/sections/kit/motion";
import { routePath } from "@/lib/routes";

/** Migration teaser — one card: the promise, the pair (1:1 paths → 0 reprints). */
export default function MigrationTeaserSection() {
  const rootRef = useRef<HTMLDivElement>(null);

  useWholeRowBlocks(rootRef);
  useBandRowAlign(rootRef);
  useEnterStagger(rootRef, "[data-mig-item]", { y: 18, stagger: 0.1 });

  return (
    <SectionShell className="md:pt-px md:pb-px">
      <div ref={rootRef}>
        <div data-grid-block>
          <div className="grid items-center gap-8 rounded-ref-lg border border-line bg-surface px-8 py-9 shadow-ref-lg md:px-10 lg:grid-cols-[minmax(0,1fr)_auto] lg:gap-12">
            <div data-mig-item>
              <div className="font-jetbrains text-[10.5px] font-semibold uppercase tracking-[0.14em] text-brand-600">
                Migration
              </div>
              <h3 className="mt-2.5 text-[26px] font-extrabold tracking-[-0.02em]">
                Bring everything, lose nothing.
              </h3>
              <p className="mb-4 mt-2.5 max-w-[460px] text-sm leading-[1.6] text-muted [text-wrap:pretty]">
                We migrate your links for you — paths and destinations recreated on your
                own domain. A dedicated onboarding engineer runs the cutover with you.
              </p>
              <div className="flex flex-wrap gap-x-5 gap-y-2 text-[13.5px] font-semibold">
                <a
                  href={`${routePath("learn")}/migrating-50k-links`}
                  className="text-brand-600 hover:text-brand-700"
                >
                  How to migrate 50,000 links →
                </a>
                <a
                  href={routePath("compareBitly")}
                  className="text-brand-600 hover:text-brand-700"
                >
                  rii.link vs your current tool →
                </a>
              </div>
            </div>

            <div
              data-mig-item
              className="flex items-center gap-[22px] self-center rounded-ref border border-line-2 bg-surface-2 px-7 py-[22px]"
            >
              <div className="text-center">
                <div className="font-jetbrains text-[30px] font-bold tracking-[-0.02em]">
                  1:1
                </div>
                <div className="mt-1 font-jetbrains text-[10.5px] uppercase tracking-[0.08em] text-dim">
                  paths recreated
                </div>
              </div>
              <span aria-hidden className="text-lg text-brand-600">
                →
              </span>
              <div className="text-center">
                <div className="font-jetbrains text-[30px] font-bold tracking-[-0.02em] text-data-green">
                  0
                </div>
                <div className="mt-1 font-jetbrains text-[10.5px] uppercase tracking-[0.08em] text-dim">
                  reprints needed
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </SectionShell>
  );
}
